import { FC, useCallback } from 'react';
import { Box, Menu, Tabs, TabList, Tab, TabPanels, TabPanel, useDisclosure, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import { switchProfileTo } from 'modules/user/userSlice';
import ProfileCard from '../shared/ProfileCard';
import { DropdownMenuLink, setOverlayStyles } from '../shared/StyledItems';
import ListItem from '../shared/ListItem';
import { CaretRight } from 'components/Icons/Icons';
import { List as ListIcon } from 'components/Icons/Icons';
import { ReactComponent as CloseIcon } from 'assets/svgs/close.svg';
import { RootState } from 'store';
import MenuButton from '../shared/MenuButton';
import { StyledMenuList, ListItemsWrapper } from '../shared/StyledItems';
import SettingsMenu from '../SettingsMenu';
import { ReactComponent as LogOutIcon } from 'assets/svgs/logout.svg';
import { logout } from 'modules/auth/authSlice';
import useActiveLink from 'hooks/useActiveLink';
import { hasAccessToCompanySettings } from 'utils/hasAccessToCompanySettings';
import ContextSwitcher from '../ContextSwitcher';

const profileTypes: ProfileType[] = ['Person', 'Company'];

interface Props {
  profileItems: MenuItem[];
  companyItems: MenuItem[];
  contextItems: ContextMenuItem[];
  currentContext: PortalContext;
  languages: string[] | undefined;
}

const StyledCloseIcon = styled(CloseIcon)`
  width: 18px;
  height: 18px;
  path {
    stroke: white;
    stroke-width: 1.5px;
    stroke-linecap: round;
  }
`;

const MobileMenu: FC<Props> = ({ profileItems, companyItems, contextItems, currentContext, languages }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentAccount, currentProfile } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { activeLink, handleActiveLink } = useActiveLink();

  const profilePictureUrl = currentAccount?.profile?.pictureUrl;
  const companyPictureUrl = undefined;
  const hasAccess = hasAccessToCompanySettings(companyItems.length);

  const handleProfileType = (index: number): void => {
    dispatch(switchProfileTo(profileTypes[index]));
  };

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, []);

  const getTabIndex = (): number => {
    return profileTypes.findIndex(profileType => profileType === currentProfile?.type);
  };

  return (
    <Box
      display={{ base: 'block', lg: 'none' }}
      mr="16px"
      sx={{
        _before: {
          ...setOverlayStyles(isOpen),
          opacity: isOpen ? '0.2' : '0',
        },
      }}
    >
      <Menu autoSelect={false} placement="bottom-end" isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
        <MenuButton isActive={!!activeLink && !isOpen} zIndex="1001" activeLinkHighlightPadding={4}>
          {isOpen ? (
            <Flex as="span" boxSize="40px" justifyContent="center" alignItems="center" bgColor="primary.500" borderRadius="4px">
              <StyledCloseIcon />
            </Flex>
          ) : (
            <ListIcon width="40px" height="32px" />
          )}
        </MenuButton>
        <StyledMenuList w="290px" zIndex="1001">
          <ListItemsWrapper>
            <Tabs onChange={handleProfileType} index={getTabIndex()}>
              <TabList>
                <Tab px="0">
                  <ProfileCard profileType="Person" pictureUrl={profilePictureUrl} />
                </Tab>
                {hasAccess && (
                  <Tab px="0" ml="auto">
                    <ProfileCard profileType="Company" pictureUrl={companyPictureUrl} />
                  </Tab>
                )}
              </TabList>
              <TabPanels>
                {!!profileItems.length && (
                  <TabPanel as="ul" p="16px 0 0">
                    {profileItems.map(item => (
                      <ListItem as="li" key={`${profileTypes[0]}_${item.id}`}>
                        <DropdownMenuLink to={item.link} isActive={(match: unknown) => handleActiveLink(item.text, match)} onClick={onClose}>
                          {item.text}
                          <CaretRight />
                        </DropdownMenuLink>
                      </ListItem>
                    ))}
                  </TabPanel>
                )}
                {hasAccess && (
                  <TabPanel as="ul" p="16px 0 0">
                    {companyItems.map(item => (
                      <ListItem as="li" key={`${profileTypes[1]}_${item.id}`}>
                        <DropdownMenuLink to={item.link} isActive={(match: unknown) => handleActiveLink(item.text, match)} onClick={onClose}>
                          {item.text}
                          <CaretRight />
                        </DropdownMenuLink>
                      </ListItem>
                    ))}
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </ListItemsWrapper>
          {languages && languages.length > 1 && (
            <ListItemsWrapper mt="8px" px="0">
              <SettingsMenu isMobile languages={languages} />
            </ListItemsWrapper>
          )}
          {contextItems.length > 1 && (
            <ListItemsWrapper mt="8px" p="13px 0">
              <ContextSwitcher items={contextItems} current={currentContext} isMobile />
            </ListItemsWrapper>
          )}
          <ListItemsWrapper mt="8px">
            <Flex
              as="button"
              justifyContent="space-between"
              alignItems="center"
              w="100%"
              onClick={handleLogout}
              sx={{ _hover: { cursor: 'pointer' } }}
            >
              {t('menu.logout')}
              <LogOutIcon />
            </Flex>
          </ListItemsWrapper>
        </StyledMenuList>
      </Menu>
    </Box>
  );
};

export default MobileMenu;
